/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { Button, PageNotViewable } from '@pwc/pwc-deals-ui';
import {
  Constants,
  ErrorUtilities,
  TextUtilities,
} from '@pwc/pwc-deals-web-common';
import classNames from 'classnames';
import {
  pick as _pick,
  defaultsDeep as _defaultsDeep,
  filter as _filter,
  find as _find,
  get as _get,
  isEmpty as _isEmpty,
  cloneDeep as _cloneDeep,
  isEqual as _isEqual,
  has as _has,
  debounce,
} from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';
import SectionArea from 'common/components/SectionArea/SectionArea';
import { TABLEAU_LIBRARY_ITEM_TYPES } from 'common/components/SectionArea/libraryItemUtils.ts';
import { layoutConfigs } from 'common/components/Section/Section/constants';
import { UNABLE_TO_ADD_OBJECT } from 'common/components/ModalError/constants';
import { deviceViewports } from 'common/constants/DeviceViewports';
import { getViewClientInfo } from 'common/helpers/viewClientVersion';
import { useApprovalWorkflow } from 'common/hooks/useApprovalWorkflow';
import useMobile from 'common/hooks/useMobile';
import useWindowSize from 'common/hooks/useWindowSize';
import {
  showModalError,
  showMoreMenuError,
  showBannerError,
  showGlobalError,
  hideGlobalError,
  hideBannerError,
} from 'common/redux/errors/errorsSlice';
import {
  createTopic,
  updateTopic,
  updateTopicById,
  setIsSaving,
  setIsSavingFulfilled,
  setIsSavingRejected,
  clearIsSaving,
  selectTopicSavingState,
} from 'common/redux/project-overview/projectOverviewSlice';
import { getUserViewStatusCountsForProject } from 'common/services/projectService';
import { v4 as uuidv4 } from 'uuid';
import {
  getErrorMessageDuplicateTitle,
  TITLE_DUPLICATION_CONTROL_FAILED,
  // eslint-disable-next-line import/extensions
} from 'common/helpers/duplicateTitleError';
import { projectsContentsSearch } from 'features/analyses/analysesService';
import CONSTANTS from 'features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/Constants';
import { generateTargetDroppableLocationIdsNew } from 'features/analyses/Freeform/FreeformAnalysisLayout/generateTargetDroppableLocationIds';
import {
  clearClipboard,
  getLibraryItemsFromClipboard,
  postRichText,
} from 'features/contentLibrary/libraryItemService';
import BannerError from 'common/components/BannerError';
import { getWorkflowStatus } from 'features/workflow/workflowService';
import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';
import { useDirtyCheck } from '@pwc/auto-save-ui';
import { useEventHub, useEventSubscription } from '@pwc/event-hub-ui';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import { getPowerBiReports } from '../../../analyses/getPowerBiReportsSlice';
import { fetchUserViewCountsSucceeded } from '../../projectSlice';
import { getTopicVersions, updateTopicStatus } from '../../topicService';
import './ProjectOverviewModule.scss';
import { mapTopic } from './Topic/mapTopic';
import TopicHeader from './Topic/TopicHeader/TopicHeader';
import TopicPanel, { getClassName } from './Topic/TopicPanel/TopicPanel';
import {
  fetchExcelItems,
  fetchPptItems,
  fetchRichTextItems,
  getExcelPPTItem,
  getDynamicExcelObject,
} from './utils/helper';
import AutoSave from '../../../../common/components/AutoSave.tsx';
import {
  AUTO_SAVE_LOCK_ERROR,
  AUTO_SAVE_IDLE_ERROR,
} from '../../../../common/components/GlobalError/constants';
import featureFlags, {
  isDisplayFormatEnabled,
} from '../../../../common/helpers/featureFlags';
import useLock from '../../../../common/hooks/useLock.tsx';
import { POWER_PIVOT_LIBRARY_ITEM_TYPES } from '../../../analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';

const ProjectOverviewModule = ({
  isNewTopic,
  topic,
  topicLayout,
  libraryItemGroups,
  onDelete,
  onEdit,
  projectId,
  onTopicStatusUpdated,
  onViewSpecificVersion,
  onMove,
  moveOption,
  handleOpenPanel,
  onSelectionChange,
  resetPanelLayout,
  showEmptyClipboardModal,
  setShowEmptyClipboardModal,
  onDeleteDraftTopic,
  onSave,
  onUpdate,
  isCreatingOrUpdating,
  isOnClientVersion,
}) => {
  const libraryItemsLocal = useRef();
  const isMobile = useMobile();
  // TODO store hypotheses in useState
  const { hypotheses } = { hypotheses: [] };
  const [versions, setVersions] = useState(null);
  const [hasContentError, setHasContentError] = useState(false);
  const [viewClient, setViewClient] = useState({});
  const [isLoading, setIsLoading] = useState(null);
  const [libraryItemGroupArray, setLibraryItemGroupArray] = useState(null);
  const [draftTopic, setDraftTopic] = useState(_cloneDeep(topic));
  const [isEditMode, setIsEditMode] = useState(topic.isDraftTopic);
  const [isTitle, setIsTitle] = useState(topic?.content?.title?.length > 0);
  const [topicError, setTopicError] = useState(false);
  const [topicErrorMessage, setTopicErrorMessage] = useState('');
  const [originalTitle] = useState(draftTopic?.content?.title);
  const { width } = useWindowSize();
  const dispatch = useDispatch();
  const draft = Constants.VERSION_STATUS.DRAFT.value;
  const shared = Constants.VERSION_STATUS.SHARED.value;
  const isMounted = useRef(true);
  const [initialForm, setInitialForm] = useState();
  const dirtyCheck = useDirtyCheck(initialForm);
  const [autoSaveError, setAutoSaveError] = useState('');
  const [libraryItemLoadedCount, setLibraryItemLoadedCount] = useState(0);
  const [formatTypeList, setFormatTypeList] = useState({});
  const [contextStatusInfoItem, setContextStatusInfoItem] = useState({
    shareWithAll: isOnClientVersion,
    versionStatus: topic.content.versionStatus,
    userViewStatus: topic.content.userViewStatus,
    isEditMode: !!isEditMode,
    isViewMode: !isEditMode,
  });
  const { isSaving = false } = useSelector(
    selectTopicSavingState(topic.content.contentThreadId)
  );
  const disableSaveWhileSaving =
    featureFlags.isAutoSaveEnabled && isEditMode && isSaving;

  const { name: userName } = useLock({
    contentThreadId: topic.content.contentThreadId,
  });

  const { eventHub } = useEventHub();
  const { t } = useTranslation();
  const mergeTopic = useCallback(
    (newTopic) => {
      // eslint-disable-next-line
      const newTopicPanels = [..._get(newTopic, ['content', 'panels'], [])];

      const updatedPanels = draftTopic.content.panels.map((panel) => {
        const panelToMerge = _find(
          newTopicPanels,
          (_panel) => _panel.panelNumber === panel.panelNumber
        );

        if (panelToMerge) {
          return _defaultsDeep({}, panelToMerge, panel);
        }
        return panel;
      });

      const newPanels = _filter(
        newTopicPanels,
        (_panel) =>
          !_find(
            updatedPanels,
            (panel) => _panel.panelNumber === panel.panelNumber
          )
      );

      const panelsToReplace = [...updatedPanels, ...newPanels];

      panelsToReplace.sort((a, b) => {
        return a.panelNumber - b.panelNumber;
      });

      if (newTopic.content) {
        delete newTopic.content.panels;
      }

      const newDraftTopic = _defaultsDeep(
        {
          content: {
            panels: panelsToReplace,
          },
        },
        newTopic,
        draftTopic
      );

      if (isMounted.current) {
        setDraftTopic(newDraftTopic);
      }
    },
    [draftTopic]
  );

  useEffect(() => {
    if (libraryItemGroups) {
      dispatch(getPowerBiReports(projectId));
    }
  }, [projectId, libraryItemGroups]);

  useEffect(() => {
    const cloneTopic = _cloneDeep(topic);
    const newDraftTopic = _defaultsDeep(
      {
        isDraftTopic: cloneTopic.isDraftTopic,
        content: {
          contentThreadId: cloneTopic.content.contentThreadId,
          versionStatus: cloneTopic.content.versionStatus,
          userViewStatus: cloneTopic.content.userViewStatus,
          versionNumber: cloneTopic.content.versionNumber,
          dateModified: cloneTopic.content.dateModified,
          modifiedBy: cloneTopic.content.modifiedBy,
        },
      },
      draftTopic
    );
    setDraftTopic(newDraftTopic);
    // eslint-disable-next-line
  }, [
    topic.content.dateModified,
    topic.content.versionStatus,
    topic.content.versionNumber,
    topic.content.contentThreadId,
    topic.isDraftTopic,
    setDraftTopic,
  ]);

  // eslint-disable-next-line no-unused-vars
  const moveTopic = useCallback(
    (fromIdx, toIdx) => {
      const newTopicsPanelsOrder = draftTopic.content.panels.slice();
      newTopicsPanelsOrder[fromIdx] = _cloneDeep(
        draftTopic.content.panels[toIdx]
      );
      newTopicsPanelsOrder[toIdx] = _cloneDeep(
        draftTopic.content.panels[fromIdx]
      );
      const panels = newTopicsPanelsOrder
        .map((p, idx) => ({
          ...p,
          panelNumber: idx + 1,
        }))
        .filter((p) => !!p);
      const newTopic = {
        ...draftTopic,
        content: {
          ...draftTopic.content,
          panels,
        },
      };
      setDraftTopic(newTopic);
    },
    [draftTopic]
  );

  const targetDroppableLocationIds = useMemo(
    () =>
      generateTargetDroppableLocationIdsNew({
        layoutType: topicLayout,
        order: 1,
      }),
    [topicLayout]
  );

  const searchTitleDuplicity = useCallback(
    async (topicTitle) => {
      if (!topicTitle.length) {
        setTopicError(false);
        setTopicErrorMessage('');
        return false;
      }
      if (isTitleRestricted(topicTitle)) {
        setTopicError(true);
        setTopicErrorMessage(restrictedErrorMessage);
        return false;
      }
      const trimmedTitle = TextUtilities.trimText(topicTitle);
      try {
        const matchItems = await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        });
        if (
          matchItems &&
          matchItems.length > 0 &&
          TextUtilities.trimText(matchItems[0].title).toLowerCase() ===
            trimmedTitle.toLowerCase() &&
          draftTopic.content.contentThreadId !== matchItems[0].contentThreadId
        ) {
          setTopicError(true);
          setTopicErrorMessage(getErrorMessageDuplicateTitle(matchItems[0], t));
          return false;
        }
        setTopicError(false);
        setTopicErrorMessage('');
      } catch (e) {
        setTopicError(true);
        setTopicErrorMessage(
          ErrorUtilities.getErrorMessage(e, t(TITLE_DUPLICATION_CONTROL_FAILED))
        );
        return false;
      }

      return true;
    },
    [projectId, draftTopic]
  );

  const debouncedSearchTitleDuplicity = useMemo(
    () => debounce(searchTitleDuplicity, 250),
    [searchTitleDuplicity]
  );

  const handleTopicTitleUpdate = (title) => {
    setIsTitle(title.trim().length > 0);
    debouncedSearchTitleDuplicity(title);
    mergeTopic({
      content: { title },
    });
  };

  const fetchUpdatedCountsForProject = async () => {
    const userViewCountsResponse = await getUserViewStatusCountsForProject(
      projectId
    );
    dispatch(fetchUserViewCountsSucceeded(userViewCountsResponse));
  };

  const handleLibraryItemUpdate = (
    libraryItem,
    panelNumber,
    excelPptLibraryItem = {}
  ) => {
    const topicToMerge = {
      content: {
        panels: [
          {
            ...libraryItem,
            panelNumber,
            libraryItemId: libraryItem.id,
            contentType: libraryItem.type,
            status: 'abc',
          },
        ],
      },
    };

    if (!_isEmpty(excelPptLibraryItem)) {
      if (isPowerPivotLibraryItem(libraryItem.type)) {
        const dynamicExcelLibraryItemList = _get(
          draftTopic,
          ['libraryItemGroups', 'dynamicExcelLibraryItemList'],
          []
        );
        topicToMerge.libraryItemGroups = {
          dynamicExcelLibraryItemList:
            dynamicExcelLibraryItemList.concat(excelPptLibraryItem),
        };
      } else {
        const excelLibraryItemList = _get(
          draftTopic,
          ['libraryItemGroups', 'excelPptLibraryItemList'],
          []
        );
        topicToMerge.libraryItemGroups = {
          excelPptLibraryItemList:
            excelLibraryItemList.concat(excelPptLibraryItem),
        };
      }
    }
    mergeTopic(topicToMerge);
  };

  const handleRichTextContentUpdate = (panelNumber, text, linkedObjects) => {
    const panelToMerge = {
      panelNumber,
      content: text,
      contentType: 'text',
      childLibraryItems: linkedObjects.map((l) => l.id),
    };

    const index = draftTopic.content.panels.findIndex(
      (x) => x.panelNumber === panelNumber
    );

    if (index > -1) {
      draftTopic.content.panels[index] = {
        ...draftTopic.content.panels[index],
        ...panelToMerge,
      };
      setDraftTopic(_cloneDeep(draftTopic));
    }

    /**
     * -  track the number of editors that have loaded their library items
     * -  this will be used to determine if auto can be enabled to ensure the initial data is complete
     */
    setLibraryItemLoadedCount((value) => value + 1);
  };

  const getVersions = useCallback(async () => {
    if (!topic?.content?.contentThreadId) {
      return undefined;
    }
    try {
      const versionsResponse = await getTopicVersions(
        projectId,
        topic.content.contentThreadId
      );
      if (versionsResponse && versionsResponse.length > 0) {
        return versionsResponse;
      }
      if (!versionsResponse) {
        throw new Error('failed to retrieve topic versions');
      }
      return [];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return [];
    }
  }, [projectId, topic.content.contentThreadId]);

  const loadVersions = useCallback(async () => {
    const versionsResponse = await getVersions();
    if (versionsResponse) {
      setVersions(versionsResponse);
    }
  }, [getVersions, setVersions]);

  useEffect(() => {
    return () => dispatch(hideBannerError());
  }, []);

  useEffect(() => {
    isMounted.current = true;

    if (isMounted.current) {
      loadVersions();
    }

    return () => {
      isMounted.current = false;
    };
  }, [getVersions, loadVersions, topic.contentId, topic.dateModified]);

  const getDraftVersion = () => {
    if (versions && versions.length > 0) {
      const draftObject = versions.find(
        (version) =>
          version.versionStatus !== Constants.VERSION_STATUS.SHARED.value
      );
      if (draftObject) {
        return draftObject;
      }
    }
    return null;
  };

  const getSharedVersion = () => {
    if (versions && versions.length > 0) {
      const draftObject = versions.find(
        (version) =>
          version.versionStatus === Constants.VERSION_STATUS.SHARED.value
      );
      if (draftObject) {
        return draftObject;
      }
    }
    return null;
  };

  const currentVersion = useMemo(
    () => (versions && versions.length === 1 ? versions[0] : null),
    [versions]
  );

  const draftVersion = useMemo(getDraftVersion, [versions]);
  const sharedVersion = useMemo(getSharedVersion, [versions]);

  // TODO refactor!
  const typeToContentType = (type) => {
    switch (type) {
      case 100:
        return 'text';
      case 300:
        return '300';
      case 400:
        return '400';
      default:
        return '?';
    }
  };

  const addItemsToTopicPanels = (topicToAddItems, items) => {
    const { panels } = topicToAddItems.content;
    const newPanels = panels.map((panel) => {
      const clonedPanel = _cloneDeep(panel);
      if (items) {
        const selectedItems = items.filter(
          (item) => item.id === clonedPanel.libraryItemId
        );
        if (selectedItems.length > 0) {
          const item = selectedItems[0];
          const displaySwitcherForFormat = [301, 302].includes(item.type);
          return {
            ...clonedPanel,
            content: item.content,
            contentType: typeToContentType(item.type),
            autoSync: item.autoSync,
            errorCode: item?.errorCode,
            error: item.error,
            statusType: item.type,
            contentsWithFormatType: displaySwitcherForFormat
              ? selectedItems.map((x) =>
                  _pick(x, [
                    'content',
                    'type',
                    'formatType',
                    'id',
                    'isDisplayedFormatType',
                  ])
                )
              : undefined,
          };
        }
      }

      return clonedPanel;
    });
    return newPanels;
  };

  const addItemsToTopic = useCallback(
    (items) => {
      const panels = addItemsToTopicPanels(draftTopic, items);
      mergeTopic({
        content: {
          panels,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftTopic, mergeTopic]
  );

  useEffect(() => {
    const clientInfo = getViewClientInfo(
      () => onViewSpecificVersion(shared, sharedVersion.contentId),
      () => onViewSpecificVersion(draft, draftVersion.contentId),
      versions,
      topic && topic.content.versionNumber,
      topic && topic.content.versionStatus
    );

    const viewClientInfo = {
      ...clientInfo,
      isOnClientVersion,
    };

    setViewClient(viewClientInfo);
  }, [
    topic,
    versions,
    onViewSpecificVersion,
    draft,
    shared,
    draftVersion,
    sharedVersion,
    isOnClientVersion,
  ]);

  const isShared = useMemo(() => {
    return topic.versionStatus === 3;
  }, [topic]);

  useEffect(
    () => {
      setContextStatusInfoItem({
        shareWithAll: isOnClientVersion,
        versionStatus: topic.content.versionStatus,
        userViewStatus: topic.content.userViewStatus,
        isEditMode: !!isEditMode,
        isViewMode: !isEditMode,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditMode]
  );

  const getDraftContent = useCallback(
    async (editMode = undefined) => {
      const { excelPptLibraryItemList = [], richTextLibraryItemList = [] } =
        libraryItemGroups || {};
      let excelData;
      let richTextData;
      let pptData;

      if (excelPptLibraryItemList && excelPptLibraryItemList.length > 0) {
        [excelData, pptData] = await Promise.all([
          fetchExcelItems(projectId, excelPptLibraryItemList),
          fetchPptItems(projectId, excelPptLibraryItemList),
        ]);

        if (isDisplayFormatEnabled && !_isEmpty(formatTypeList)) {
          excelData = excelData.map((excelItem) => {
            let newExcelItem = excelItem;
            if (formatTypeList[excelItem.id]) {
              newExcelItem = {
                ...excelItem,
                isDisplayedFormatType:
                  formatTypeList[excelItem.id][excelItem.formatType],
              };
            }
            return newExcelItem;
          });
        }
      }

      if (richTextLibraryItemList && richTextLibraryItemList.length > 0) {
        richTextData = await fetchRichTextItems(
          projectId,
          richTextLibraryItemList,
          {
            ...contextStatusInfoItem,
            isEditMode: !!editMode,
          }
        );
      }
      const excelLibraryItems = excelData || [];
      const richTextLibraryItems = richTextData || [];
      const pptLibraryItems = pptData || [];
      libraryItemsLocal.current = []
        .concat(excelLibraryItems)
        .concat(richTextLibraryItems)
        .concat(pptLibraryItems);
      addItemsToTopic(libraryItemsLocal.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [addItemsToTopic, libraryItemGroups, projectId]
  );

  const fetchDraftContent = async () => {
    try {
      setIsLoading(true);
      await getDraftContent();
      setIsLoading(false);
      setLibraryItemGroupArray(libraryItemGroups);

      setHasContentError(false);
    } catch (error) {
      setIsLoading(false);
      setHasContentError(true);
    }
  };

  useEffect(() => {
    if (
      isMounted.current &&
      !isShared &&
      !isLoading &&
      libraryItemGroups &&
      !_isEqual(libraryItemGroupArray, libraryItemGroups)
    ) {
      fetchDraftContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItemGroups]);

  const onFormatTypeChange = (
    panel,
    libraryItemId,
    newFormatType,
    needMergePanels = false
  ) => {
    const contentsWithFormatType = panel.contentsWithFormatType.map(
      (libItem) => {
        return {
          ...libItem,
          isDisplayedFormatType:
            libItem.id === libraryItemId
              ? libItem.formatType === newFormatType
              : libItem.isDisplayedFormatType,
        };
      }
    );

    if (contentsWithFormatType && isDisplayFormatEnabled) {
      formatTypeList[libraryItemId] = Object.fromEntries(
        new Map(
          contentsWithFormatType.map((obj) => [
            obj.formatType,
            obj.isDisplayedFormatType,
          ])
        ).set()
      );
      setFormatTypeList(formatTypeList);
    }
    if (needMergePanels) {
      mergeTopic({
        content: {
          panels: [
            {
              ...panel,
              contentsWithFormatType,
            },
          ],
        },
      });
    }
  };

  useEffect(() => {
    if (isShared && !_isEqual(draftTopic, topic)) {
      mergeTopic(topic);
      setLibraryItemGroupArray(null);
    }
  }, [topic, isShared, draftTopic, mergeTopic]);

  const workflowActions = useApprovalWorkflow(
    getWorkflowStatus,
    async (_projectId, contentId, step) => {
      try {
        await updateTopicStatus(_projectId, `${contentId}`, step);
      } catch (e) {
        dispatch(
          showMoreMenuError({
            itemId: contentId,
            moreMenuErrorType:
              Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE,
          })
        );
        return;
      }
      const versionsResponse = await getVersions();
      if (versionsResponse) {
        const sharedInResponse = versionsResponse.find(
          (v) => v.versionStatus === shared
        );
        const draftInResponse = versionsResponse.find(
          (v) => v.versionStatus !== shared
        );
        if (!draftInResponse) {
          const { contentId: sharedContentId } = sharedInResponse;
          onTopicStatusUpdated(topic, shared, sharedContentId);
        } else {
          const { contentId: draftContentId } = draftInResponse;
          onTopicStatusUpdated(topic, draft, draftContentId);
        }
      }
    },
    projectId,
    (draftVersion && draftVersion.contentId) ||
      (currentVersion && currentVersion.contentId),
    topic,
    versions
  );

  const handleAddExcelOrPPT = async (panelNumber) => {
    try {
      const libraryItemResponse = await getLibraryItemsFromClipboard(projectId);
      if (!libraryItemResponse) {
        setShowEmptyClipboardModal(true);
        return;
      }
      const { data, id, name, status, type, ...restOf } = libraryItemResponse;

      let libraryItemData = {
        GroupedByKey: data.driveId,
        DriveId: data.driveId,
        DriveItemId: data.driveItemId,
        SharePointItemUrl: data.sharePointItemUrl,
        SiteId: data.siteId,
        CellRange: data.cellRange,
      };
      switch (type) {
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            Name: data.name,
            NamedRange: data.name,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            ChartName: data.chartName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            FormatType: data.formatType,
            Name: data.name,
            UniqueId: data.uniqueId,
            WorksheetName: data.worksheetName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
          libraryItemData = {
            ...libraryItemData,
            SlideNumber: data.slideNumber,
            GroupName: data.groupName,
          };
          break;
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelCellRange:
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelChart:
        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelNamedRange:
          libraryItemData = {
            ...libraryItemData,
            ...data,
          };
          break;
        default:
          break;
      }

      const libraryItemCreated = {
        data: JSON.stringify(libraryItemData),
        analysisColumn: 1,
        formatType: data.formatType,
        id,
        name,
        status,
        type,
      };

      if (isPowerPivotLibraryItem(type)) {
        const dynamicExcelItem = {
          dynamicExcelLibraryItems: [
            {
              id,
              name,
              type,
              data: {
                ...libraryItemData,
              },
            },
          ],
          groupedByKey: libraryItemData.GroupedByKey,
          driveId: libraryItemData.DriveId,
          driveItemId: libraryItemData.DriveItemId,
          sharePointItemUrl: libraryItemData.SharePointItemUrl,
          siteId: libraryItemData.SiteId,
        };

        handleLibraryItemUpdate(
          libraryItemCreated,
          panelNumber,
          dynamicExcelItem
        );
      } else {
        const excelPptLibraryItem = {
          excelPptLibraryItems: [
            {
              id,
              name,
              type,
              data: {
                cellRange: libraryItemData.CellRange,
                chartName: libraryItemData.ChartName,
                embeddedUrl: libraryItemData.embeddedUrl,
                errorCode: libraryItemData.errorCode,
                formatType: libraryItemData.FormatType,
                groupName: libraryItemData.GroupName,
                name: libraryItemData.Name,
                slideNumber: libraryItemData.SlideNumber,
                uniqueId: libraryItemData.UniqueId,
                worksheetName: libraryItemData.WorksheetName,
              },
            },
          ],
          groupedByKey: libraryItemData.GroupedByKey,
          driveId: libraryItemData.DriveId,
          driveItemId: libraryItemData.DriveItemId,
          sharePointItemUrl: libraryItemData.SharePointItemUrl,
          siteId: libraryItemData.SiteId,
        };

        handleLibraryItemUpdate(
          libraryItemCreated,
          panelNumber,
          excelPptLibraryItem
        );
      }
      clearClipboard(projectId, {
        data,
        id,
        name,
        status,
        type,
        ...restOf,
      });
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const onAddPBIItem = (pbiItem, panelNumber) => {
    handleLibraryItemUpdate(pbiItem, panelNumber);
  };

  const onAddTableauItem = (tableauItem, panelNumber) => {
    if (
      tableauItem.allTabsSelected &&
      tableauItem.tabName &&
      tableauItem.tabDisplayName
    ) {
      // if import all tabs option is selected then tabName and tabDisplayName properties cannot be in the object
      // otherwise there are scenarios that only a single tab is displayed even though import all tabs were selected
      delete tableauItem.tabName;
      delete tableauItem.tabDisplayName;
    }
    mergeTopic({
      content: {
        panels: [
          {
            ...tableauItem,
            panelNumber,
            type: tableauItem.allTabsSelected
              ? TABLEAU_LIBRARY_ITEM_TYPES.TableauReport
              : TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage,
            content: tableauItem.data,
            contentType: tableauItem.type,
            // status: 'abc'
          },
        ],
      },
    });
  };

  const handleAddTextContent = async (newPanelNumber) => {
    try {
      const name = uuidv4();
      const richTextData = JSON.stringify({
        RichTextData: '',
      });

      const id = await postRichText(projectId, name, richTextData);
      if (!id) {
        dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
      } else {
        mergeTopic({
          content: {
            panels: [
              {
                libraryItemId: id,
                panelNumber: newPanelNumber,
                content: '',
                contentType: 'text',
                type: 1,
                data: JSON.stringify({
                  RichTextData: Constants.EMPTY_RICH_TEXT_PLACEHOLDER,
                }),
              },
            ],
          },
        });
      }
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const handleRemoveContent = (panelNumber) => {
    const updatedPanels = draftTopic.content.panels.map((panel) => {
      if (panel.panelNumber === panelNumber) {
        return {
          ...panel,
          oldType: panel.type !== '' ? panel.type : panel.oldType,
          content: '',
          type: '',
          contentType: '',
          data: '',
          RichTextData: '',
        };
      }
      return panel;
    });

    mergeTopic({
      content: {
        panels: updatedPanels,
      },
    });
  };

  const updateExcelPPListWithChangedFormatTypes = () => {
    let updatedExcelPPTList = _cloneDeep(
      draftTopic.libraryItemGroups?.excelPptLibraryItemList
    );
    if (isDisplayFormatEnabled && !_isEmpty(formatTypeList)) {
      updatedExcelPPTList = updatedExcelPPTList.map((group) => {
        const updatedItems = group.excelPptLibraryItems.map((excelItem) => {
          const changed = {
            ...excelItem,
            data: {
              ...excelItem?.data,
              formatType: formatTypeList[excelItem.id]
                ? (formatTypeList[excelItem.id].SVG && 'SVG') || 'HTML'
                : excelItem?.data.formatType,
            },
          };
          return changed;
        });
        return { ...group, excelPptLibraryItems: updatedItems };
      });
    }
    return updatedExcelPPTList;
  };

  const updateExistingTopic = (
    topicToSaveDto,
    skipSavingIndicator = false,
    isDoneAction
  ) => {
    dispatch(hideBannerError());
    if (!skipSavingIndicator) {
      dispatch(setIsSaving({ contentThreadId: topic.content.contentThreadId }));
    }
    dispatch(
      updateTopicById({
        projectId,
        topicThreadId: draftTopic.content.contentThreadId,
        topicToSaveDto,
      })
    )
      .unwrap()
      .then(async ({ response }) => {
        const versionDetails = draftTopic.content.versionStatus ===
          Constants.VERSION_STATUS.SHARED.value && {
          versionStatus: 1,
          versionNumber: draftTopic.content.versionNumber + 1,
          userViewStatus: 1,
        };
        const versionsFromBE = await getVersions();
        const modifiedInfo = _pick(
          versionsFromBE.find(
            (v) =>
              v.versionNumber ===
              (versionDetails?.versionNumber ||
                draftTopic.content.versionNumber)
          ),
          ['dateModified', 'modifiedBy']
        );

        const updatedExcelPPTList = updateExcelPPListWithChangedFormatTypes();

        const changes = {
          ...draftTopic,
          content: {
            ...draftTopic.content,
            ...topicToSaveDto,
            ...modifiedInfo,
            panels: topicToSaveDto.panels.map((p) => ({
              ...(draftTopic.content.panels.find(
                (x) => x.panelNumber === p.panelNumber
              ) || {}),
              ...p,
            })),
            contentId: response.contentId,
            ...versionDetails,
          },
          isDraftTopic: featureFlags.isAutoSaveEnabled && !isDoneAction, // keep topic in edit mode when auto save feature flag is enabled
          libraryItemGroups: {
            ...draftTopic.libraryItemGroups,
            excelPptLibraryItemList: updatedExcelPPTList,
          },
        };
        setDraftTopic(changes);
        dispatch(
          updateTopic({
            id: draftTopic.content.contentThreadId,
            changes,
          })
        );
        onUpdate(topic.content.contentId, changes?.content);
        fetchUpdatedCountsForProject();
        if (!skipSavingIndicator) {
          dispatch(
            setIsSavingFulfilled({
              contentThreadId: topic.content.contentThreadId,
            })
          );
        }

        if (!featureFlags.isAutoSaveEnabled) {
          /**
           * When the feature flag is disabled
           *  Then take the user back to view mode after updating the topic
           */
          setIsEditMode(false);
        }
      })
      .catch((_error) => {
        dispatch(showBannerError('Failed to update content'));
        dispatch(
          setIsSavingRejected({
            contentThreadId: topic.content.contentThreadId,
          })
        );
      });
  };

  const createNewTopic = async (topicToSaveDto) => {
    dispatch(
      createTopic({
        projectId,
        topicThreadId: topic.content.contentId,
        topicToSaveDto,
      })
    )
      .unwrap()
      .then(({ response }) => {
        setVersions([
          {
            contentId: response,
            versionNumber: 1,
            versionStatus: 1,
          },
        ]);
        const updatedExcelPPTList = updateExcelPPListWithChangedFormatTypes();

        const changes = {
          ...draftTopic,
          content: {
            ...draftTopic.content,
            ...topicToSaveDto,
            panels: topicToSaveDto.panels.map((p) => ({
              ...(draftTopic.content.panels.find(
                (x) => x.panelNumber === p.panelNumber
              ) || {}),
              ...p,
            })),
          },
          libraryItemGroups: {
            ...draftTopic.libraryItemGroups,
            excelPptLibraryItemList: updatedExcelPPTList,
          },
        };

        onSave(topic.content.contentId, response, changes);
        setIsEditMode(false);
        fetchUpdatedCountsForProject();
      });
  };

  const handleOnSave = async (
    skipSavingIndicator = false,
    isDoneAction = false
  ) => {
    if (isEditMode) {
      if (
        draftTopic &&
        draftTopic.content?.title?.toLowerCase() !==
          originalTitle?.toLowerCase()
      ) {
        const isUniqueTitle = await searchTitleDuplicity(
          draftTopic.content.title
        );
        if (!isUniqueTitle) {
          return;
        }
      }
      try {
        const topicToSaveDto = mapTopic({
          topicLayoutTypeValue: topicLayout,
          contentTypeArray: draftTopic.content.panels.map(
            (panel) => panel.type
          ),
          richTextArray: draftTopic.content.panels.map((panel) => ({
            content: panel.content || panel.sharedContent || '',
            childLibraryItems: panel.childLibraryItems?.filter(Boolean) || [],
            libraryItemId: panel.libraryItemId || undefined,
          })),
          hypothesisArray: [],
          excelChartIdArray: draftTopic.content.panels.map(
            (panel) => panel.libraryItemId || ''
          ),
          topicTitle: draftTopic.content.title,
          panelNumbersArray: draftTopic.content.panels.map(
            (panel) => panel.panelNumber
          ),
        });
        await getDraftContent(false);
        if (isNewTopic) {
          /**
           * This will only execute when auto save feature flag is disabled
           * because when auto save feature flag is enabled new topics
           * will never be rendered in this component, instead new topics
           * will be auto created by the AutoCreate component
           */
          await createNewTopic(topicToSaveDto);
        } else {
          await updateExistingTopic(
            topicToSaveDto,
            skipSavingIndicator,
            isDoneAction
          );
        }
        // after we saved everything
        // clean the sharedContent on the topics
        if (topic && topic.content && topic.content.panels) {
          topic.content.panels.forEach((p) => {
            p.sharedContent = null;
          });
        }
        if (draftTopic && draftTopic.content && draftTopic.content.panels) {
          draftTopic.content.panels.forEach((p) => {
            p.sharedContent = null;
          });
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error('Error while saving', e);
      }
    }
  };

  const currentData = useMemo(
    () =>
      mapTopic({
        topicLayoutTypeValue: topicLayout,
        contentTypeArray: draftTopic.content.panels.map((panel) => panel.type),
        richTextArray: draftTopic.content.panels.map((panel) => ({
          content: panel.content || panel.sharedContent || '',
          childLibraryItems: panel.childLibraryItems || [],
        })),
        hypothesisArray: [],
        excelChartIdArray: draftTopic.content.panels.map(
          (panel) => panel.libraryItemId || ''
        ),
        topicTitle: draftTopic.content.title,
        panelNumbersArray: draftTopic.content.panels.map(
          (panel) => panel?.panelNumber
        ),
      }),
    [topicLayout, draftTopic]
  );

  const handleDone = async () => {
    if (dirtyCheck.dataIsDirty(currentData)) {
      await handleOnSave(true, true); // skip auto save indicator; this prevents the saved indicator from displaying when user re-enters into edit mode
    }

    dispatch(
      clearIsSaving({
        contentThreadId: topic.content.contentThreadId,
      })
    );

    /**
     * -  This will address a race condition with the release lock API
     * -  By deferring it for 250ms, the lock is successfully released
     */
    setTimeout(() => {
      setIsEditMode(false);
    }, 250);
  };

  const canBeSaved = useMemo(() => {
    if (!isTitle || topicError) {
      return false;
    }

    const panelsWithEditors = draftTopic.content.panels.filter(
      (panel) => panel.contentType === 'text'
    );

    if (panelsWithEditors.length === 0) {
      /**
       * When there are no editors
       *  Then there is no need to verify that all library items have been loaded
       *  simply returning true will allow auto save to be enabled
       */
      return true;
    }

    /**
     * When there are editors
     *  Then there is a need to verify that all library items have been loaded
     */
    const editorsWithIngestion = panelsWithEditors.filter((panel) => {
      const content = new DOMParser().parseFromString(
        panel.content,
        'text/html'
      );
      const placeholders = content.getElementsByClassName('placeholder');

      return placeholders.length !== 0;
    });

    if (libraryItemLoadedCount < editorsWithIngestion.length) {
      /**
       *  - libraryItemLoadedCount is used to track how many editors have loaded their library items
       *  - if all library items have not been loaded then return true so auto save does not get enabled
       *  - this will prevent the first dirty check resulting in true and triggering an auto save
       */
      return false;
    }

    // if execution reached this far then allow auto save to be enabled
    return true;
  }, [draftTopic.content.panels, isTitle, libraryItemLoadedCount, topicError]);

  const mouse = useRef(null);
  const handleMouseMove = (e) => {
    mouse.current = e;
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const addRemoveThumbNailClass = (draggableId, cssClass, dragMode) => {
    const draggableDiv = document.querySelector(
      `[data-rbd-draggable-id="${draggableId}"]`
    ).children[0];

    if (draggableDiv) {
      draggableDiv.setAttribute('class', cssClass);

      if (dragMode !== 'SNAP') {
        const position = draggableDiv.getBoundingClientRect();
        draggableDiv.style.left = `${mouse.current.clientX - position.left}px`;
        draggableDiv.style.top = `${mouse.current.clientY - position.top}px`;
        draggableDiv.style.position = 'absolute';
      }
    }

    return draggableDiv;
  };

  const handleDragEnd = ({ destination, source, draggableId }) => {
    const draggableDiv = addRemoveThumbNailClass(
      draggableId,
      'library-item-card2'
    );

    const isValid =
      destination && source && destination.droppableId && source.droppableId;

    if (isValid) {
      const isNewDropLocation =
        destination.droppableId !== source.droppableId &&
        destination.index !== source.index;
      if (isNewDropLocation) {
        const toIdx = destination.droppableId.split('-').slice(-2)[0];
        const fromIdx = source.droppableId.split('-').slice(-2)[0];

        if (toIdx !== fromIdx) {
          moveTopic(fromIdx - 1, toIdx - 1);
        }
      } else {
        draggableDiv.style = {};
      }
    } else {
      draggableDiv.style = {};
    }
  };

  const isEditModeViewable = width >= deviceViewports.tablet && isEditMode;

  const handleCancel = async () => {
    // Can't cancel new topic, so delete it as it's not saved.
    if (draftTopic.isDraftTopic) {
      return onDeleteDraftTopic(draftTopic.content.contentId);
    }
    // Restore original topic and reset local changes.
    resetPanelLayout();
    setIsEditMode(false);
    try {
      await getDraftContent(false);
    } catch (error) {
      setHasContentError(true);
    }

    const orgTopic = _cloneDeep(topic);
    const items = libraryItemsLocal.current;
    orgTopic.content.panels = addItemsToTopicPanels(orgTopic, items);
    setDraftTopic(orgTopic);
    return null;
  };

  const handleOnDragStart = ({ draggableId, mode }) => {
    addRemoveThumbNailClass(draggableId, 'library-item-card2 thumb-nail', mode);
  };

  const isSaveDisabled =
    (!isEditMode || !canBeSaved || isCreatingOrUpdating) && !isTitle;
  const isGridLayout =
    topicLayout === Constants.SECTION_LAYOUT_TYPE.TWO_COLUMN_L_50_2A_R_50_1A;

  const renderViewContentPanels = useMemo(() => {
    if (!isEditMode && !_isEmpty(_get(draftTopic, ['content', 'panels'], []))) {
      const panelNumbersArray = draftTopic.content.panels.map(
        (panel) => panel.panelNumber
      );
      const panelNumberMax = Math.max(...panelNumbersArray);
      const placeholderArray = Array(panelNumberMax).fill(null);

      const content = placeholderArray.map((_, index) => {
        const panel = draftTopic.content.panels.find(
          (p) => p.panelNumber === index + 1
        );

        let hypothesis;
        let excelPPTItem;
        let dynamicExcelObject;
        if (
          panel &&
          panel?.type === Constants.TOPIC_PANEL_TYPES.KEY_FOCUS_AREA.value
        ) {
          hypothesis =
            panel.hypothesis ||
            hypotheses.find((h) => h.contentThreadId === panel.contentThreadId);
        }

        if (panel) {
          excelPPTItem = getExcelPPTItem(topic, panel);
          dynamicExcelObject = getDynamicExcelObject(
            topic,
            panel,
            draftTopic.items,
            index
          );
        }
        if (
          panel &&
          panel.contentType?.toLowerCase?.() === 'tableau' &&
          panel.sharedContent &&
          draftTopic.items
        ) {
          let currentItem = draftTopic.items[index];
          if (!_has(currentItem, 'data')) {
            currentItem = draftTopic.items.find(
              (draftItem) => draftItem.id === panel.libraryItemId
            );
          }
          panel.reportId =
            _get(JSON.parse(currentItem.data ?? '{}'), 'ReportId', undefined) ||
            _get(
              JSON.parse(currentItem.data ?? '{}'),
              'OriginalReportId',
              undefined
            );

          panel.tabName = _get(
            JSON.parse(currentItem.data ?? '{}'),
            'TabName',
            undefined
          );
          const originalPanel = { ...panel };

          Object.assign(panel, currentItem, originalPanel);
        }

        const classNameForEmptyPanel = getClassName(topicLayout, index + 1);

        return (
          <SyncedInfoProvider
            value={contextStatusInfoItem}
            key={`${topic.content.contentId}-${index + 1}`}
          >
            {panel ? (
              <TopicPanel
                key={`${topic.content.contentId}-${panel.panelNumber}`}
                topic={draftTopic}
                topicLayout={topicLayout}
                panel={panel}
                hypothesis={hypothesis}
                projectId={projectId}
                loading={isLoading}
                excelPPTItem={excelPPTItem}
                hasContentError={hasContentError}
                dynamicExcelObject={dynamicExcelObject}
              />
            ) : (
              <div
                data-testid="topic-panel"
                className={classNameForEmptyPanel}
              />
            )}
          </SyncedInfoProvider>
        );
      });
      const layoutConfig = layoutConfigs[topicLayout];
      return isGridLayout ? (
        // eslint-disable-next-line react/jsx-indent
        <div className={`section-grid ${layoutConfig.containerClass}`}>
          {content}
        </div>
      ) : (
        content
      );
    }
    return <></>;
  }, [
    isEditMode,
    draftTopic,
    topicLayout,
    isGridLayout,
    topic,
    projectId,
    isLoading,
    hasContentError,
    hypotheses,
    contextStatusInfoItem,
  ]);

  useEffect(() => {
    if (canBeSaved && isEditModeViewable && !initialForm) {
      setInitialForm(currentData);
    }
  }, [canBeSaved, isEditModeViewable, initialForm, currentData]);

  useEffect(() => {
    if (canBeSaved && !isEditModeViewable && initialForm) {
      // clear initial form when going back to view mode
      setInitialForm(null);
    }
  }, [canBeSaved, initialForm, isEditModeViewable]);

  const globalErrorClassName = `global-error-placeholder-${topic.content.contentThreadId}`;

  useEffect(() => {
    if (autoSaveError) {
      dispatch(
        showGlobalError({
          eventType: 'banner',
          id: `.${globalErrorClassName}`,
          error: { code: autoSaveError },
          metadata: {
            bannerMetadata: { alertLevel: 'info', bannerPosition: 'bottom' },
          },
          userName,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveError, userName]);

  const handleAutoSaveError = (error) => {
    setIsEditMode(false);
    setAutoSaveError(error);
  };

  const eventMatchesContext = (event) => {
    const {
      data: { contentThreadId: contentThreadIdToSave },
    } = event;

    const {
      content: { contentThreadId },
    } = topic;

    return contentThreadIdToSave === contentThreadId;
  };

  const miniTableInProgress = () => {
    const {
      content: { panels = [] },
    } = draftTopic;

    const panelsWithMiniLoader = panels.filter((panel) =>
      panel?.childLibraryItems?.find((item) => item === 'miniloader')
    );

    return panelsWithMiniLoader.length !== 0;
  };

  useEventSubscription('autosave-save', (event) => {
    if (!eventMatchesContext(event)) {
      return;
    }

    if (miniTableInProgress()) {
      return;
    }

    handleOnSave();
    eventHub?.publish('comment-reload-comment-threads', {});
    dirtyCheck.setLastData(currentData);
  });

  useEventSubscription('autosave-idle', (event) => {
    if (!eventMatchesContext(event)) {
      return;
    }

    handleAutoSaveError(AUTO_SAVE_IDLE_ERROR);
  });

  const saveDoneButtonText = featureFlags.isAutoSaveEnabled ? 'Done' : 'Save';

  const clearAutoSaveError = () => {
    setAutoSaveError('');
    dispatch(hideGlobalError());
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleOnDragStart}>
      <AutoSave
        enableAutoLockContext
        disableAutoSaveOverride={!isEditModeViewable}
        projectId={projectId}
        contentThreadId={topic.content.contentThreadId}
        isReady={isEditModeViewable && initialForm}
        isValid={isTitle && !topicError}
        initialData={initialForm}
        getData={() => currentData}
      >
        {({ acquireLockState: { isError } }) => {
          if (isError) {
            handleAutoSaveError(AUTO_SAVE_LOCK_ERROR);
            return <></>;
          }
          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className={classNames(
                'project-overview-module',
                !isEditMode ? 'view-mode' : ''
              )}
              key={topic.content.contentThreadId}
              data-testid="project-overview-module"
              onMouseDown={() => {
                onSelectionChange(draftTopic);
              }}
            >
              <TopicHeader
                topic={draftTopic}
                topicLayout={topicLayout}
                isEditMode={isEditMode}
                isLoading={isLoading}
                onDelete={onDelete}
                onEdit={() => {
                  clearAutoSaveError();
                  setIsEditMode(true);
                  onEdit();
                  // we can assume draftTopic to be non-draft when editMode is true
                  setDraftTopic({ ...draftTopic, isDraftTopic: false });
                }}
                onChange={handleTopicTitleUpdate}
                workflowActions={workflowActions}
                viewClient={viewClient}
                onMove={onMove}
                moveOption={moveOption}
                handleOpenPanel={handleOpenPanel}
                topicError={topicError}
                topicErrorMessage={topicErrorMessage}
              />
              <div
                className={
                  !isEditMode && !isGridLayout
                    ? 'project-overview-module__inner'
                    : ''
                }
                data-testid="project-overview-module-inner"
                data-topic-id={topic.content.contentThreadId}
              >
                {isMobile && isEditMode && <PageNotViewable />}
                {isEditModeViewable && (
                  <div
                    className={classNames(
                      'section__middle-row',
                      {
                        [CONSTANTS.LAYOUT_TYPE_TO_SECTION_CLASSNAME_MAP[
                          topicLayout
                        ]]: topicLayout,
                      },
                      isEditMode ? 'edit-mode' : ''
                    )}
                    data-testid="section-middle-row"
                  >
                    {targetDroppableLocationIds &&
                      targetDroppableLocationIds.map(
                        (droppableLocationId, index) => {
                          const panelNumber = index + 1;

                          const loadedPanel =
                            draftTopic.content.panels.find(
                              (panel) => panel.panelNumber === panelNumber
                            ) || {};

                          const hasContent =
                            draftTopic.content.panels.length ===
                            targetDroppableLocationIds.length;

                          const excelPPTItem = getExcelPPTItem(
                            draftTopic,
                            loadedPanel
                          );
                          const dynamicExcelObject = getDynamicExcelObject(
                            draftTopic,
                            loadedPanel
                          );

                          return (
                            <React.Fragment key={droppableLocationId}>
                              {droppableLocationId && (
                                <SyncedInfoProvider
                                  value={contextStatusInfoItem}
                                >
                                  <SectionArea
                                    key={droppableLocationId}
                                    droppableLocationId={droppableLocationId}
                                    // TODO remove panel number
                                    panelNumber={panelNumber}
                                    areaClass={droppableLocationId}
                                    onRichTextContentUpdate={
                                      handleRichTextContentUpdate
                                    }
                                    panel={loadedPanel}
                                    topic={topic}
                                    libraryItemMap={{}}
                                    panels={draftTopic.content.panels}
                                    droppableLocations={[]}
                                    onInsertExcelOrPPT={handleAddExcelOrPPT}
                                    onAddPBIItem={onAddPBIItem}
                                    onAddTableauItem={onAddTableauItem}
                                    onAddTextContent={handleAddTextContent}
                                    onRemoveContent={handleRemoveContent}
                                    showEmptyClipboardModal={
                                      showEmptyClipboardModal
                                    }
                                    onEmptyModalHide={() => {
                                      setShowEmptyClipboardModal(false);
                                    }}
                                    projectId={projectId}
                                    hasContent={hasContent}
                                    isPowerBiEnabled
                                    excelPPTItem={{
                                      ...excelPPTItem,
                                    }}
                                    objectInjestPopoverAddButtonTitle="Add an item"
                                    dynamicExcelObject={{
                                      ...dynamicExcelObject,
                                      status: dynamicExcelObject
                                        ? dynamicExcelObject.sharedStatus
                                        : undefined,
                                    }}
                                    onFormatTypeChange={onFormatTypeChange}
                                  />
                                </SyncedInfoProvider>
                              )}
                            </React.Fragment>
                          );
                        }
                      )}
                  </div>
                )}
                {renderViewContentPanels}
              </div>
              {isEditModeViewable && (
                <div className="topic-footer">
                  <div className="separator" />
                  <BannerError />
                  <div className="topic-footer__buttons">
                    {!featureFlags.isAutoSaveEnabled && (
                      <Button
                        data-testid="topic-cancel-button"
                        type="button"
                        onClick={handleCancel}
                        className="ui-button button-link form-cancel-button"
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      data-testid="topic-edit-button"
                      type="button"
                      onClick={handleDone}
                      className="ui-button button-small"
                      disabled={
                        isSaveDisabled || topicError || disableSaveWhileSaving
                      }
                    >
                      {isEditMode ? saveDoneButtonText : 'Add'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        }}
      </AutoSave>
    </DragDropContext>
  );
};

export default ProjectOverviewModule;
